import { useEffect } from "react";
import { createPortal } from "react-dom";
import Footer from "./footer.jsx";
import { useScrollLock } from "./primitives.jsx";
import { useMeta } from "../seo.js";
import { ROUTE_META } from "../routeMeta.js";
import { MAIN_HOST, SITE_URL } from "../siteAddress.js";

// The story behind the book's title, framed against the news peg: in 2025 the
// Yankees finally retired the no-beard rule Greg fought in 1977. A deliberate
// SEO + conversion asset — a primary-source teaser that ranks for the
// policy/searches and funnels to the book. It tells just enough; the full story
// is the book's title chapter. All quotes are verbatim from the memoir.
//
// Same content, two shells: <ShavePage> is the standalone route served to
// direct visits / crawlers / shared links (real indexable URL); <ShaveModal> is
// the in-app overlay so reading it from the site never reloads the page. The
// content lives once, in <ShaveStory>, so the two can't drift.

const BOOK_COVER =
  "https://m.media-amazon.com/images/S/compressed.photo.goodreads.com/books/1554949076i/45018799.jpg";

const ARTICLE_LD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "The Day the Yankees Made Me Shave",
  description: ROUTE_META.shave.description,
  image: `${SITE_URL}/assets/greg-yankees.jpg`,
  datePublished: "2026-06-02",
  author: { "@type": "Person", "@id": `${SITE_URL}/#greg`, name: "Greg Pryor" },
  publisher: { "@id": `${SITE_URL}/#greg` },
  mainEntityOfPage: SITE_URL + ROUTE_META.shave.path,
  about: ["New York Yankees appearance policy", "Mustache Revolt", "George Steinbrenner"],
  mentions: {
    "@type": "Book",
    "@id": `${SITE_URL}/#book`,
    name: "The Day The Yankees Made Me Shave",
  },
};

// Per-route <head> + Article structured data, shared by the page and the modal
// so an open story always reflects the right title/canonical/OG and schema.
function useShaveSeo() {
  useMeta({ ...ROUTE_META.shave, ogType: "article" });
  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.textContent = JSON.stringify(ARTICLE_LD);
    document.head.appendChild(ld);
    return () => ld.remove();
  }, []);
}

// The story content — no page chrome. `onBuy`, when supplied (modal context),
// intercepts the primary CTA so it closes the overlay and scrolls to the book
// section in place instead of doing a full navigation.
function ShaveStory({ onBuy }) {
  return (
    <article className="shave-article">
      <header className="shave-hero">
        <div className="mono shave-kicker">1977 · The Bronx</div>
        <h1 className="display shave-title">The Day the Yankees Made Me Shave</h1>
        <p className="shave-dek">
          In 2025, the Yankees finally retired the no-beard rule that stood for nearly fifty years.
          In 1977, they handed it to a 28-year-old infielder who had no intention of going quietly.
        </p>
      </header>

      <div className="shave-body">
        <p>
          In the spring of '77 I got traded to the New York Yankees — the Bronx Zoo. I wore a
          mustache all through big-league camp, same as Thurman Munson, Reggie Jackson, and Catfish
          Hunter. Then they optioned me to the minors, where the farm director informed me there was
          a ban on facial hair. I had to shave it off.
        </p>

        <blockquote className="shave-pull">
          “As I was shaving it off, I kept repeating one phrase to myself:{" "}
          <em>I will get you back.</em>”
        </blockquote>

        <p>
          The Mustache Revolt was born that day. I was 28 years old, broke, and staring down a third
          straight season in Triple-A. I didn't want a mustache. I wanted <em>out</em>. So I called
          a team meeting in Syracuse and asked every man on the roster whether he thought he was man
          enough to grow one.
        </p>

        <blockquote className="shave-pull shave-pull-lead">
          “Is Thurman Munson more of a man than you?”
        </blockquote>

        <p>
          Every hand went up. They signed a joint statement. We grew them. The Yankees threatened to
          replace the entire Triple-A roster over a few mustaches — and that was only the beginning
          of what that revolt set loose.
        </p>

        <h2 className="shave-h2">Then I sat across from George Steinbrenner.</h2>

        <p>
          When the Yankees finally wanted me at the negotiating table, they offered me a two-year
          deal with a guaranteed September call-up. I turned it down. The Boss could not believe a
          broke kid would walk away from the pinstripes.
        </p>

        <blockquote className="shave-pull shave-pull-boss">
          “Son, I am putting you right at the top of my dumbass list.”
          <cite>— George M. Steinbrenner</cite>
        </blockquote>

        <p>
          Eight years later, I was a World Series champion with the Kansas City Royals. I'll never
          know whether that ring happens if I sign Steinbrenner's deal. That's the thing about a
          fork in the road — you only get to take one.
        </p>
      </div>

      <aside className="shave-cta">
        <a
          className="shave-cta-cover"
          href="/#book"
          onClick={onBuy}
          aria-label="The Day The Yankees Made Me Shave — get the book"
        >
          <img src={BOOK_COVER} alt="The Day The Yankees Made Me Shave — book cover" />
        </a>
        <div className="shave-cta-copy">
          <div className="mono shave-cta-kicker">The whole story</div>
          <h2 className="display shave-cta-title">How the revolt ended is the title chapter.</h2>
          <p>
            The full Mustache Revolt — plus 26 other days that made a ten-year big-league career,
            from the Pine Tar Game to the '85 Series — is in the book. Every copy signed by hand, in
            real ink.
          </p>
          <div className="shave-cta-row">
            <a className="shave-btn shave-btn-primary" href="/#book" onClick={onBuy}>
              Get the signed book →
            </a>
          </div>
        </div>
      </aside>

      <p className="shave-context">
        <strong>Why this still matters:</strong> George Steinbrenner's appearance policy took hold
        in the mid-1970s and outlasted nearly every player it ever touched — until the Yankees
        finally relaxed it in 2025. I was fighting to get mustaches approved for all minor leaguers
        in 1977. I share my Mustache Revolt story in my book.
      </p>
    </article>
  );
}

// Standalone route — served on a direct visit, a reload, a shared link, or a
// crawler. The story is the page's primary content.
export default function ShavePage() {
  useShaveSeo();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <main className="shave-page grain">
        <header className="shave-nav">
          <a href="/" className="shave-brand">
            <span className="shave-brand-mark">GP</span>
            <span>Greg Pryor</span>
          </a>
          <a className="shave-home-link mono" href="/">
            Back to the site
          </a>
        </header>
        <div className="shave-page-body">
          <ShaveStory />
          <a className="mono shave-back" href="/">
            ← Back to {MAIN_HOST}
          </a>
        </div>
      </main>
      <Footer />
    </>
  );
}

// In-app overlay — opened from the homepage so reading never reloads the page.
// Escape / backdrop / the close button all dismiss it (which restores the URL
// via the caller). The page beneath stays mounted, so closing returns the
// visitor to the exact spot they were reading from.
export function ShaveModal({ onClose }) {
  useShaveSeo();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Lock the page beneath the modal (iOS-safe); restores the scroll position
  // and forces a repaint on close so Safari can't leave the page blank.
  useScrollLock();

  // Primary CTA inside the modal: close the overlay — no navigation, no reload.
  // The overlay is only ever opened from the callout in the homepage's book
  // section, and closing restores that exact scroll position, so the reader
  // lands right back on the book + its buy controls.
  const goToBook = (e) => {
    e.preventDefault();
    onClose();
  };

  return createPortal(
    <div
      className="shave-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="shave-modal-panel grain"
        role="dialog"
        aria-modal="true"
        aria-label="The Day the Yankees Made Me Shave"
      >
        <button className="shave-modal-close" onClick={onClose} aria-label="Close">
          <svg width="15" height="15" viewBox="0 0 14 14" aria-hidden="true">
            <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.6" fill="none" />
          </svg>
        </button>
        <ShaveStory onBuy={goToBook} />
      </div>
    </div>,
    document.body,
  );
}

const shaveStyles = typeof document !== "undefined" ? document.createElement("style") : null;
if (shaveStyles) {
  shaveStyles.textContent = `
.shave-page {
  min-height: 100vh;
  background: var(--navy-900);
  color: var(--bone);
}
.shave-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 22px clamp(20px, 5vw, 64px);
  border-bottom: 1px solid var(--rule);
}
.shave-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: var(--bone);
  font-family: var(--serif);
  font-weight: 600;
  font-size: 1.125rem;
}
.shave-brand-mark {
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--royal-blue);
  border: 1px solid var(--gold);
  font-family: var(--mono);
  font-size: 0.75rem;
  color: var(--bone);
}
.shave-home-link {
  color: var(--gold-bright);
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
}
.shave-home-link:hover { color: var(--bone); }

.shave-page-body {
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(48px, 9vw, 104px) clamp(20px, 6vw, 40px) 80px;
}
.shave-article { color: var(--bone); }
.shave-kicker {
  color: var(--gold);
  font-size: 0.75rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  margin: 0 0 22px;
}
.shave-title {
  font-size: clamp(2.375rem, 7.5vw, 4.5rem);
  line-height: 1.03;
  letter-spacing: -0.01em;
  color: var(--bone);
  margin: 0 0 28px;
}
.shave-dek {
  font-family: var(--serif);
  font-size: clamp(1.1875rem, 2.4vw, 1.4375rem);
  line-height: 1.5;
  color: var(--bone-dim);
  margin: 0;
  max-width: 60ch;
}

.shave-body { margin-top: clamp(40px, 7vw, 60px); }
.shave-body > p {
  font-family: var(--serif);
  font-size: 1.1875rem;
  line-height: 1.72;
  color: var(--bone-dim);
  margin: 0 0 28px;
}
.shave-body em { color: var(--bone); font-style: italic; }

.shave-pull {
  margin: 40px 0;
  padding: 4px 0 4px 28px;
  border-left: 3px solid var(--gold);
  font-family: var(--serif);
  font-size: clamp(1.4375rem, 3.4vw, 1.875rem);
  line-height: 1.32;
  color: var(--bone);
}
.shave-pull em { color: var(--gold-bright); font-style: italic; }
.shave-pull-lead {
  border-left: none;
  padding: 0;
  text-align: center;
  font-size: clamp(1.6875rem, 4.6vw, 2.625rem);
  font-weight: 600;
  color: var(--gold-bright);
  margin: 48px 0;
}
.shave-pull-boss { font-size: clamp(1.5rem, 3.6vw, 2rem); }
.shave-pull-boss cite {
  display: block;
  margin-top: 14px;
  font-family: var(--mono);
  font-style: normal;
  font-size: 0.8125rem;
  letter-spacing: 0.08em;
  color: var(--bone-dim);
}

.shave-h2 {
  font-family: var(--serif);
  font-weight: 600;
  font-size: clamp(1.625rem, 3.6vw, 2.125rem);
  line-height: 1.2;
  color: var(--bone);
  margin: 52px 0 24px;
}

.shave-cta {
  display: grid;
  grid-template-columns: 176px 1fr;
  gap: clamp(24px, 4vw, 40px);
  align-items: center;
  margin: clamp(44px, 7vw, 64px) 0 48px;
  padding: clamp(26px, 4vw, 38px);
  background: var(--navy-800);
  border: 1px solid var(--rule-strong);
  border-radius: 14px;
}
.shave-cta-cover {
  display: block;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.5);
  transition: transform 220ms ease;
}
.shave-cta-cover:hover { transform: translateY(-4px); }
.shave-cta-cover img { display: block; width: 100%; height: auto; }
.shave-cta-kicker {
  color: var(--gold);
  font-size: 0.6875rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin: 0 0 12px;
}
.shave-cta-title {
  font-size: clamp(1.375rem, 3vw, 1.75rem);
  line-height: 1.12;
  color: var(--bone);
  margin: 0 0 14px;
}
.shave-cta-copy > p {
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.6;
  color: var(--bone-dim);
  margin: 0 0 22px;
}
.shave-cta-row { display: flex; flex-wrap: wrap; gap: 12px; }
.shave-btn {
  display: inline-block;
  padding: 13px 22px;
  font-family: var(--mono);
  font-size: 0.75rem;
  letter-spacing: 0.12em;
  text-decoration: none;
  border-radius: 7px;
  transition: background 200ms, color 200ms, border-color 200ms;
}
.shave-btn-primary {
  background: var(--gold);
  color: var(--navy-900);
  border: 1px solid var(--gold);
}
.shave-btn-primary:hover { background: var(--gold-bright); border-color: var(--gold-bright); }
.shave-btn-ghost {
  background: transparent;
  color: var(--bone);
  border: 1px solid var(--rule-strong);
}
.shave-btn-ghost:hover { border-color: var(--gold); color: var(--gold-bright); }

.shave-context {
  border-top: 1px solid var(--rule);
  padding-top: 32px;
  font-family: var(--serif);
  font-size: 1rem;
  line-height: 1.66;
  color: var(--bone-dim);
  margin: 0;
}
.shave-context strong { color: var(--bone); }
.shave-back {
  display: inline-block;
  margin-top: 28px;
  color: var(--gold-bright);
  text-decoration: none;
  font-size: 0.75rem;
  letter-spacing: 0.14em;
}
.shave-back:hover { color: var(--bone); }

/* --- In-app overlay --- */
.shave-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(3, 8, 18, 0.72);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: clamp(16px, 5vh, 56px) 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: shave-fade 200ms ease;
}
@keyframes shave-fade { from { opacity: 0; } to { opacity: 1; } }
.shave-modal-panel {
  position: relative;
  width: 100%;
  max-width: 720px;
  background: var(--navy-900);
  border: 1px solid var(--rule-strong);
  border-radius: 16px;
  box-shadow: 0 40px 120px rgba(0, 0, 0, 0.6);
  padding: clamp(28px, 5vw, 56px) clamp(22px, 5vw, 56px) clamp(32px, 5vw, 56px);
  animation: shave-rise 240ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes shave-rise { from { transform: translateY(14px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
.shave-modal-panel .shave-title { font-size: clamp(2rem, 6vw, 3.25rem); }
.shave-modal-panel .shave-body { margin-top: clamp(32px, 5vw, 48px); }
.shave-modal-close {
  position: sticky;
  top: 0;
  float: right;
  display: grid;
  place-items: center;
  width: 38px;
  height: 38px;
  margin: -8px -8px 0 16px;
  border-radius: 50%;
  border: 1px solid var(--rule-strong);
  background: var(--navy-800);
  color: var(--bone-dim);
  cursor: pointer;
  transition: color 180ms, border-color 180ms, background 180ms;
}
.shave-modal-close:hover { color: var(--bone); border-color: var(--gold); background: var(--navy-700); }

@media (max-width: 620px) {
  .shave-cta { grid-template-columns: 1fr; text-align: center; }
  .shave-cta-cover { max-width: 168px; margin: 0 auto; }
  .shave-cta-row { justify-content: center; }
}
`;
  document.head.appendChild(shaveStyles);
}
